"use client";

/**
 * SearchContactsDisplay - Contact search results display
 */

import { useState } from "react";
import { Card, Tag, Avatar } from "@/ui";
import { colors, spacing, radius, textStyles, primitives } from "@/ui/tokens";
import { Business, Email, Phone, SearchOff } from "@mui/icons-material";
import {
  ToolCard,
  ToolHeader,
  ToolBody,
  ToolProgress,
  ToolError,
  ToolEmptyState,
} from "../../Tool";
import { deriveToolState } from "@/lib/sola/types/tools";
import type { ToolDisplayProps } from "@/lib/sola/types/tools";
import { showAllButtonClass, displayValue } from "../utils";

interface Contact {
  id: string | number;
  name?: string;
  company?: string | { id?: number; name?: string; [key: string]: unknown };
  email?: string;
  phone?: string;
  title?: string;
  location?: string;
  tags?: Array<string | { id?: number; name?: string; [key: string]: unknown }>;
  avatar?: string;
}

interface SearchOutput {
  success?: boolean;
  contacts?: Contact[];
  totalCount?: number;
  message?: string;
}

function ContactCard({ contact }: { contact: Contact }) {
  return (
    <Card
      variant="outlined"
      padding="sm"
      style={{
        borderRadius: radius.md,
        cursor: "pointer",
        transition: "all 0.2s",
      }}
    >
      <div style={{ display: "flex", gap: spacing[4], alignItems: "flex-start" }}>
        <Avatar
          src={contact.avatar}
          name={contact.name || `Contact #${contact.id}`}
          size="md"
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <span style={{ ...textStyles.body2, fontWeight: 600, display: "block" }}>
            {contact.name || `Contact #${contact.id}`}
          </span>
          {contact.company && (
            <span style={{ ...textStyles.body2, color: colors.text.muted, display: "block" }}>
              {displayValue(contact.company)}
            </span>
          )}
          {contact.title && (
            <span style={{ ...textStyles.caption, color: colors.text.muted, display: "block" }}>
              {contact.title}
            </span>
          )}
          <div style={{ display: "flex", gap: spacing[4], marginTop: spacing[2], flexWrap: "wrap" }}>
            {contact.email && (
              <div style={{ display: "flex", alignItems: "center", gap: spacing[1] }}>
                <Email style={{ fontSize: 14, color: colors.text.muted }} />
                <span style={textStyles.caption}>{contact.email}</span>
              </div>
            )}
            {contact.phone && (
              <div style={{ display: "flex", alignItems: "center", gap: spacing[1] }}>
                <Phone style={{ fontSize: 14, color: colors.text.muted }} />
                <span style={textStyles.caption}>{contact.phone}</span>
              </div>
            )}
          </div>
          {contact.tags && contact.tags.length > 0 && (
            <div style={{ display: "flex", gap: spacing[1], flexWrap: "wrap", marginTop: spacing[2] }}>
              {contact.tags.slice(0, 3).map((tag, i) => (
                <Tag key={i} size="sm" variant="default">
                  {displayValue(tag)}
                </Tag>
              ))}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

interface StreamingOutput {
  phase?: string;
  progress?: number;
  message?: string;
}

export function SearchContactsDisplay({ toolCall, isStreaming }: ToolDisplayProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const toolState = deriveToolState(toolCall);
  const { status, isLoading, isError, error, progress } = toolState;

  const [showAll, setShowAll] = useState(false);

  const result = toolCall.finalOutput as SearchOutput | undefined;
  const contacts = result?.contacts || [];
  const totalCount = result?.totalCount || contacts.length;
  const MAX_VISIBLE = 8;
  const visibleContacts = showAll ? contacts : contacts.slice(0, MAX_VISIBLE);
  const needsTruncation = contacts.length > MAX_VISIBLE;

  // Get streaming phase message from preliminary outputs
  const lastOutput = toolCall.outputs[toolCall.outputs.length - 1];
  const streamingData = lastOutput?.preliminary
    ? (lastOutput.output as StreamingOutput)
    : null;
  const progressLabel =
    streamingData?.message || "Searching contacts...";

  return (
    <ToolCard status={status} noPadding>
      <ToolHeader
        title={`Search Contacts${!isLoading && contacts.length > 0 ? ` (${totalCount})` : ""}`}
        toolName={toolCall.toolName}
        status={status}
        progress={progress}
        isExpanded={isExpanded}
        onToggleExpand={() => setIsExpanded((prev) => !prev)}
        icon={<Business style={{ fontSize: 18 }} />}
      />

      <ToolBody isOpen={isExpanded} noPadding>
        {isLoading && (
          <div style={{ padding: spacing[5], textAlign: "center" }}>
            <ToolProgress variant="dots" label={progressLabel} />
          </div>
        )}

        {isError && error && (
          <div style={{ padding: spacing[4] }}>
            <ToolError error={error} />
          </div>
        )}

        {!isLoading && (
          <>
            <div
              style={{
                paddingLeft: spacing[5],
                paddingRight: spacing[5],
                paddingTop: spacing[3],
                paddingBottom: spacing[3],
                backgroundColor: colors.canvas.card,
                borderBottom: `1px solid ${colors.border.subtle}`,
              }}
            >
              <span style={{ ...textStyles.body2, fontWeight: 500 }}>
                {totalCount} {totalCount === 1 ? "contact" : "contacts"} found
              </span>
            </div>

            {contacts.length > 0 ? (
              <div style={{ padding: spacing[4] }}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
                    gap: spacing[4],
                  }}
                >
                  {visibleContacts.map((contact, i) => (
                    <ContactCard key={contact.id || i} contact={contact} />
                  ))}
                </div>
                {needsTruncation && !showAll && (
                  <button onClick={() => setShowAll(true)} className={showAllButtonClass}>
                    Show all {contacts.length} contacts
                  </button>
                )}
              </div>
            ) : (
              <ToolEmptyState icon={<SearchOff />} message="No contacts found" />
            )}
          </>
        )}
      </ToolBody>
    </ToolCard>
  );
}

export default SearchContactsDisplay;
